import React, {
  createContext,
  useCallback,
  useState,
  useContext,
  useEffect,
  useRef,
} from 'react';
import api from '../services/api';
import { UserGroup } from '../pages/UserGroupsPage';

const REFRESH_DEBOUNCE_MS = 30_000;

const dedupePermissions = (permissions: any[]): any[] =>
  permissions.reduce((acc: any[], permission: any) => {
    if (
      !acc.some((item: any) => item.permissionId === permission.permissionId)
    ) {
      acc.push(permission);
    }
    return acc;
  }, []);

export interface User {
  id: string;
  name: string;
  email: string;
  urlLogo: string;
  mainColor: string;
  companyId: number;
  userGroupName: string;
  isGuest: boolean;
  show_task_tutorial: boolean;
  permissions: any[];
  usergroups: any[];
  selectedUsergroup: UserGroup | undefined;
}

interface AuthState {
  token: string;
  user: User;
}

interface SignInCredentials {
  email: string;
  password: string;
}

interface AuthContextData {
  user: User;
  signIn(credentials: SignInCredentials): Promise<any>;
  signOut(): void;
  getUserGroups(user: User): Promise<User>;
  refreshUserGroups(options?: { force?: boolean }): Promise<void>;
  updateSelectedUsergroup(usergroup: any): void;
}

interface Props {
  children: React.ReactNode;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

const AuthProvider: React.FC<Props> = ({ children }) => {
  const [data, setData] = useState<AuthState>(() => {
    const token = localStorage.getItem('@Victor-insights:token');
    const user = localStorage.getItem('@Victor-insights:user');

    if (token && user) {
      api.defaults.headers.authorization = `Bearer ${token}`;
      return { token, user: JSON.parse(user) };
    }

    return {} as AuthState;
  });

  const updateSelectedUsergroup = useCallback(
    (usergroup: any) => {
      const newUser: any = {
        ...data.user,
        selectedUsergroup: usergroup,
      };

      if (usergroup) {
        newUser.mainColor = usergroup.mainColor;
        newUser.urlLogo = usergroup.urlLogo;
      }

      localStorage.setItem('@Victor-insights:user', JSON.stringify(newUser));
      setData(prev => ({
        ...prev,
        user: newUser,
      }));
    },
    [data.user],
  );

  const signOut = useCallback(() => {
    localStorage.removeItem('@Victor-insights:token');
    localStorage.removeItem('@Victor-insights:user');
    localStorage.removeItem('RSIsMute');
    localStorage.removeItem('victor-insights:chatWidget:isOpen');
    localStorage.removeItem('theme');
    // Also cleared on manual logout in menuAppBar, but the 401 interceptor
    // calls signOut directly — without this, a stale radial-menu breadcrumb
    // survives session expiry (and even a different user on the same browser).
    localStorage.removeItem('breadcrumb');

    setData({} as AuthState);
  }, []);

  const getUserGroups = useCallback(
    async (user: User) => {
      let selectedUsergroup1: any = user.selectedUsergroup
        ? user.selectedUsergroup
        : undefined;
      let usergroups: any;
      let permissionsWithoutDuplicates: any[] = [];

      const userWithData: User = user;

      let isGuest = false;
      await api
        .get('usergroups/getByEmail')
        .then(response => {
          if (response.data.isGuest === true) {
            isGuest = true;
            const allPermissions: any = response.data.guestRole[0].permissions;

            permissionsWithoutDuplicates = allPermissions.reduce(
              (acc: any, permission: any) => {
                // Verifica se já existe um objeto com o mesmo 'id' no acumulador
                if (
                  !acc.some(
                    (item: any) =>
                      item.permissionId === permission.permissionId,
                  )
                ) {
                  acc.push(permission);
                }
                return acc;
              },
              [],
            );
          } else {
            usergroups = response.data;
            let allPermissions: any[] = [];
            response.data?.forEach((userGroup: any) => {
              allPermissions = allPermissions.concat(
                userGroup.roles?.[0]?.permissions ?? [],
              );
            });
            permissionsWithoutDuplicates = allPermissions.reduce(
              (acc: any, permission: any) => {
                // Verifica se já existe um objeto com o mesmo 'id' no acumulador
                if (
                  !acc.some(
                    (item: any) =>
                      item.permissionId === permission.permissionId,
                  )
                ) {
                  acc.push(permission);
                }
                return acc;
              },
              [],
            );
            if (response.data.length === 1) {
              [selectedUsergroup1] = response.data;
            }
          }
        })
        .catch(err => {
          console.log(err);
          signOut();
        });

      userWithData.permissions = permissionsWithoutDuplicates;
      userWithData.usergroups = usergroups;
      userWithData.selectedUsergroup = selectedUsergroup1;
      userWithData.isGuest = isGuest;

      return userWithData;
    },
    [signOut],
  );

  const isRefreshingRef = useRef(false);
  const lastRefreshAtRef = useRef(0);

  const refreshUserGroups = useCallback(
    async (options?: { force?: boolean }) => {
      if (!data?.user || !data?.token) return;
      if (isRefreshingRef.current) return;
      const now = Date.now();
      if (
        !options?.force &&
        now - lastRefreshAtRef.current < REFRESH_DEBOUNCE_MS
      ) {
        return;
      }

      isRefreshingRef.current = true;
      lastRefreshAtRef.current = now;

      try {
        const response = await api.get('usergroups/getByEmail');
        const newUser: User = { ...data.user };

        if (response.data.isGuest === true) {
          newUser.isGuest = true;
          const allPermissions: any = response.data.guestRole[0].permissions;
          newUser.permissions = dedupePermissions(allPermissions);
        } else {
          newUser.isGuest = false;
          newUser.usergroups = response.data;
          const allPermissions: any[] = (response.data ?? []).flatMap(
            (ug: any) => ug.roles?.[0]?.permissions ?? [],
          );
          newUser.permissions = dedupePermissions(allPermissions);

          if (newUser.selectedUsergroup) {
            const match = response.data.find(
              (ug: any) =>
                ug.userGroupId === newUser.selectedUsergroup?.userGroupId,
            );
            if (match) {
              newUser.selectedUsergroup = match;
              newUser.mainColor = match.mainColor ?? newUser.mainColor;
              newUser.urlLogo = match.urlLogo ?? newUser.urlLogo;
            } else {
              newUser.selectedUsergroup = undefined;
            }
          } else if (response.data.length === 1) {
            [newUser.selectedUsergroup] = response.data;
          }
        }

        localStorage.setItem('@Victor-insights:user', JSON.stringify(newUser));
        setData(prev => ({ ...prev, user: newUser }));
      } catch (err) {
        // Background refresh — keep stale data on error rather than signing out.
      } finally {
        isRefreshingRef.current = false;
      }
    },
    [data?.user, data?.token],
  );

  const refreshRef = useRef(refreshUserGroups);
  useEffect(() => {
    refreshRef.current = refreshUserGroups;
  }, [refreshUserGroups]);

  useEffect(() => {
    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') {
        refreshRef.current();
      }
    };
    const onFocus = (): void => {
      refreshRef.current();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  const signIn = useCallback(
    async ({ email, password }: any) => {
      try {
        const response = await api.post('sessions', {
          email,
          password,
        });

        const { token, user } = response.data;

        if (token === 'WP-CODE') {
          return { weakPassword: true };
        }

        if (token && user) {
          localStorage.setItem('@Victor-insights:token', token);
          localStorage.setItem('@Victor-insights:user', JSON.stringify(user));

          api.defaults.headers.authorization = `Bearer ${token}`;
        }

        const userWithData: User = await getUserGroups(user);
        localStorage.setItem(
          '@Victor-insights:user',
          JSON.stringify(userWithData),
        );
        setData({ token, user: userWithData });

        if (userWithData.isGuest) {
          return { signIn: true, isGuest: true };
        }

        return { signIn: true, isGuest: false };
      } catch (error: any) {
        console.error(error);
        return { signIn: false, isGuest: false };
      }
    },
    [getUserGroups],
  );

  useEffect(() => {
    api.interceptors.response.use(
      (response: any) => response,
      (err: any) => {
        if (err.response.status === 401) {
          // console.error('Unauthorized');
          signOut();
        }

        return Promise.reject(err);
      },
    );
  }, [signOut]);

  return (
    <AuthContext.Provider
      value={{
        user: data.user,
        signIn,
        signOut,
        getUserGroups,
        refreshUserGroups,
        updateSelectedUsergroup,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

function useAuth(): AuthContextData {
  const context = useContext(AuthContext);

  return context;
}

export { AuthProvider, useAuth };
